import { Component, inject, OnInit, OnDestroy, signal, computed, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import momentTimezone from 'moment-timezone';

import { localStorageKeys, userRoleNameEnum } from '../../const/enum';
import { AuthService } from 'src/app/services/auth.service';
import { UtilService } from 'src/app/services/util.service';
import { AlertService } from 'src/app/services/alert.service';
import { Users } from '../../models/user.model';
import { ChatStore } from 'src/app/chat/chat-store.service';
import { AvatarImgComponent } from 'src/app/chat/avatar-img/avatar-img.component';

interface IncidentNotifItem {
  id: string;
  title: string;
  meta?: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [CommonModule, AvatarImgComponent],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private utilService = inject(UtilService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  chatStore = inject(ChatStore);

  protected readonly loggedInUserDetails = signal<Users>(null);
  protected readonly showIncidentNotifications = signal(false);
  protected readonly incidentNotifOpen = signal(false);
  protected readonly incidentCount = signal(0);
  protected readonly incidentLoading = signal(false);
  protected readonly incidentItems = signal<IncidentNotifItem[]>([]);
  protected readonly userMenuOpen = signal(false);

  protected readonly userInitials = computed(() => {
    const user = this.loggedInUserDetails();
    if (!user) {
      return '';
    }
    const first = (user.first_name ?? '').trim().charAt(0);
    const last = (user.last_name ?? '').trim().charAt(0);
    return (first + last).toUpperCase() || 'U';
  });

  /** A picture the user has just uploaded from Settings (shown before the stored details catch up). */
  private readonly uploadedPhoto = signal<string | null>(null);
  /** Profile picture for the avatar: just uploaded, else the one chat knows, else the one stored with the login. */
  protected readonly userPhoto = computed(() => {
    const user = this.loggedInUserDetails();
    return this.uploadedPhoto()
      || this.chatStore.avatarOf(this.chatStore.meId())
      || (user?.profile_photo || null);
  });

  private incidentSub?: Subscription;
  private incidentCountSub?: Subscription;
  private incidentListSub?: Subscription;
  private incidentLoadFallbackTimer?: ReturnType<typeof setTimeout>;


  @ViewChild('incidentNotifMenu') private incidentNotifMenu?: ElementRef<HTMLElement>;
  @ViewChild('userMenu') private userMenu?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    if (this.incidentNotifOpen() && !this.incidentNotifMenu?.nativeElement.contains(target)) {
      this.incidentNotifOpen.set(false);
    }

    if (this.userMenuOpen() && !this.userMenu?.nativeElement.contains(target)) {
      this.userMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.incidentNotifOpen()) {
      this.incidentNotifOpen.set(false);
    }
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  ngOnInit(): void {
    const loginUserDetails = this.utilService.getLocalStorage(localStorageKeys.userDetails);
    this.loggedInUserDetails.set(loginUserDetails);

    const role = this.utilService.getLocalStorage(localStorageKeys.roleType);
    // clients get a read-only view of their own sites — none of the monitoring
    // widgets in the header apply to them
    const isClient = role === userRoleNameEnum.Client;
    const canShowIncidents = role !== userRoleNameEnum.operator && !isClient;
    this.showIncidentNotifications.set(canShowIncidents);
    // Client-portal customers have no chat. For everyone else the server decides: a role with
    // chat switched off is refused, and the store then stops. The unread count shows on the
    // sidebar's Chat item; the store is started here so it counts from login.
    if (!isClient) this.chatStore.init();

    this.utilService.profilePhotoChanged.subscribe((url) => this.uploadedPhoto.set(url));

    this.utilService.userLogin.subscribe((res) => {
      if (res?.user) {
        this.loggedInUserDetails.set(res.user);
      }
    });

    if (canShowIncidents) {
      // Show the alert INCIDENT count from the batch counts API. The dashboard
      // pushes it into dashboardIncidentCount$ (kept in sync with /alerts/counts),
      // and refreshIncidentCountFromApi() seeds it on any page from the alerts
      // list totalCount so the badge is populated even off the dashboard.
      this.incidentCountSub = this.utilService.dashboardIncidentCount$.subscribe((count) => {
        this.incidentCount.set(Number(count) || 0);
      });

      this.incidentListSub = this.utilService.dashboardIncidentAlerts$.subscribe((alerts) => {
        if (this.incidentNotifOpen()) {
          this.setIncidentItemsFromAlerts(alerts);
          this.incidentLoading.set(false);
          if (this.incidentLoadFallbackTimer) {
            clearTimeout(this.incidentLoadFallbackTimer);
          }
        }
      });

      this.refreshIncidentCountFromApi();
    }
  }

  ngOnDestroy(): void {
    this.incidentSub?.unsubscribe();
    this.incidentCountSub?.unsubscribe();
    this.incidentListSub?.unsubscribe();
    if (this.incidentLoadFallbackTimer) {
      clearTimeout(this.incidentLoadFallbackTimer);
    }
  }

  toggleIncidentNotifications(event: Event): void {
    event.stopPropagation();

    const next = !this.incidentNotifOpen();
    this.incidentNotifOpen.set(next);
    if (next) {
      this.userMenuOpen.set(false);
      const cached = this.utilService.dashboardIncidentAlerts$.value;
      if (cached.length > 0) {
        this.setIncidentItemsFromAlerts(cached);
      }
      this.loadIncidentNotifications();
    }
  }

  private buildIncidentAlertsQuery(): Record<string, unknown> {
    const role = this.utilService.getLocalStorage(localStorageKeys.roleType);
    const user = this.utilService.getLocalStorage(localStorageKeys.userDetails);
    const query: Record<string, unknown> = {
      is_incident: true,
      is_seen: false,
      alert_fixed: { $ne: true },
    };
    if (role !== userRoleNameEnum.IT && role !== userRoleNameEnum.superAdmin) {
      query['control_users'] = { $in: [user?._id] };
    }
    return query;
  }

  private refreshIncidentCountFromApi(): void {
    if (!this.showIncidentNotifications()) {
      return;
    }

    this.alertService.getAlerts({
      pagination: { page: 1, perPage: 1 },
      sort: { event_timestamp: -1 },
      query: this.buildIncidentAlertsQuery(),
    }).subscribe({
      next: (res: any) => {
        const total = typeof res?.totalCount === 'number' ? res.totalCount : 0;
        if (this.utilService.dashboardIncidentCount$.value === 0 && total > 0) {
          this.utilService.dashboardIncidentCount$.next(total);
        }
      },
    });
  }

  private loadIncidentNotifications(): void {
    this.incidentLoading.set(true);
    this.incidentSub?.unsubscribe();
    if (this.incidentLoadFallbackTimer) {
      clearTimeout(this.incidentLoadFallbackTimer);
    }

    // Header owns a single fetch for the dropdown. Previously it also delegated to
    // the dashboard (refreshHeaderIncidentList$) AND ran a fallback fetch, so both
    // paths' two-phase (count + pages) queries fired the alerts API up to 4 times.
    this.incidentSub = this.fetchAllIncidentsFromApi().subscribe({
      next: (alerts) => {
        this.utilService.dashboardIncidentAlerts$.next(alerts);
        this.setIncidentItemsFromAlerts(alerts);
        this.incidentLoading.set(false);
      },
      error: () => {
        this.incidentLoading.set(false);
        this.incidentItems.set([]);
      },
    });
  }

  private fetchAllIncidentsFromApi() {
    const query = this.buildIncidentAlertsQuery();
    const sort = { event_timestamp: -1 };

    const pageSize = 50;
    // First page already carries totalCount + the first slice, so no separate
    // count query. Fetch extra pages only when there are genuinely more than one.
    return this.alertService.getAlerts({
      pagination: { page: 1, perPage: pageSize },
      sort,
      query,
    }).pipe(
      switchMap((firstRes: any) => {
        const total = typeof firstRes?.totalCount === 'number' ? firstRes.totalCount : 0;
        const firstList = firstRes?.list ?? [];
        const pages = Math.ceil(total / pageSize);
        if (pages <= 1) {
          return of(firstList);
        }

        const requests = Array.from({ length: pages - 1 }, (_, index) =>
          this.alertService.getAlerts({
            pagination: { page: index + 2, perPage: pageSize },
            sort,
            query,
          }).pipe(catchError(() => of({ list: [] })))
        );

        return forkJoin(requests).pipe(
          map((results: any[]) => [...firstList, ...results.flatMap((result) => result?.list ?? [])])
        );
      }),
      catchError(() => of([]))
    );
  }

  private setIncidentItemsFromAlerts(alerts: any[]): void {
    const sorted = [...(alerts || [])].sort(
      (a, b) => this.getIncidentSortTime(b) - this.getIncidentSortTime(a)
    );
    this.incidentItems.set(
      sorted.map((item: any) => ({
        id: item?._id ?? item?.id,
        title: this.getIncidentTitle(item),
        meta: this.getIncidentMeta(item),
      })).filter((item: IncidentNotifItem) => !!item.id)
    );
  }

  private getIncidentSortTime(alert: any): number {
    const raw = alert?.event_timestamp ?? alert?.updated_at ?? alert?.created_at;
    const ms = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(ms) ? ms : 0;
  }

  private getIncidentTitle(item: any): string {
    const site = (item?.site_name_c ?? item?.site_name ?? '—').trim();
    const camera = this.getFormattedCameraName(item);
    return `${site} - ${camera}`;
  }

  private getFormattedCameraName(alertDetails: any): string {
    const siteName = alertDetails?.site_name_c || '';
    const camName = alertDetails?.camera_name || '';
    if (camName.includes('|')) {
      const [beforePipe, afterPipe] = camName.split('|').map((part: string) => part.trim());
      if (beforePipe === siteName) {
        return afterPipe || camName;
      }
    }
    return camName || 'Camera';
  }

  private getIncidentMeta(item: any): string {
    const handledBy = item?.handledBy_User;
    const userName = handledBy
      ? `${handledBy.first_name ?? ''} ${handledBy.last_name ?? ''}`.trim()
      : '';
    const timestamp = item?.event_timestamp ?? item?.created_at;
    const when = timestamp
      ? momentTimezone(timestamp).tz('America/New_York').format('MM/DD/YYYY, HH:mm:ss')
      : '';
    if (userName && when) {
      return `${userName} - ${when}`;
    }
    return userName || when;
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    const next = !this.userMenuOpen();
    this.userMenuOpen.set(next);
    if (next) {
      this.incidentNotifOpen.set(false);
    }
  }

  openIncidentAlert(item: IncidentNotifItem): void {
    this.incidentNotifOpen.set(false);
    if (!item?.id) {
      return;
    }

    if (this.router.url.includes('/new-dashboard')) {
      this.utilService.openDashboardIncidentAlert$.next(item.id);
      return;
    }

    // readFilter: this dropdown is built from is_seen: false, so every alert in it is
    // unread — land on the INCIDENT tab's Unread pill rather than All.
    this.router.navigate(['/new-dashboard'], {
      queryParams: {
        tabNumber: 2,
        tabName: 'collapseTwo',
        alertId: item.id,
        readFilter: 'unread',
      },
    });
  }

  onLogout(): void {
    this.userMenuOpen.set(false);
    this.utilService.confirmationAlert(
      'Logout',
      'Are you sure you want to logout ? ',
      (val: boolean) => {
        if (val) {
          this.loggedInUserDetails.set(null);
          this.incidentItems.set([]);
          this.incidentCount.set(0);
          this.utilService.dashboardIncidentCount$.next(0);
          this.utilService.dashboardIncidentAlerts$.next([]);
          this.authService.logout();
        }
      },
    );
  }

  showSideBar(): void {
    this.utilService.hideShowSideBarInMobile.next(true);
  }
}
